import React, { useEffect, useMemo, useState } from "react";
import { Wallet, Download, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { money } from "@/lib/constants";
import { useLang } from "@/i18n";
import { Panel, Empty } from "@/pages/shared";

function isoWeek(dateStr) {
  const d = new Date(dateStr + "T00:00:00Z");
  const day = (d.getUTCDay() + 6) % 7;
  d.setUTCDate(d.getUTCDate() - day + 3);
  const firstThu = new Date(Date.UTC(d.getUTCFullYear(), 0, 4));
  const week = 1 + Math.round(((d - firstThu) / 86400000 - 3 + ((firstThu.getUTCDay() + 6) % 7)) / 7);
  return `${d.getUTCFullYear()}-W${String(week).padStart(2, "0")}`;
}

export default function AdminPrepayments() {
  const { t } = useLang();
  const now = new Date();
  const [mode, setMode] = useState("year");
  const [year, setYear] = useState(now.getFullYear());
  const [quarter, setQuarter] = useState(Math.floor(now.getMonth() / 3) + 1);
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [group, setGroup] = useState("month");
  const [activeQuarter, setActiveQuarter] = useState(null);
  const [rep, setRep] = useState(null);

  const query = useMemo(() => {
    if (mode === "custom") return from && to ? `start=${from}&end=${to}` : null;
    if (mode === "quarter") {
      const sm = (quarter - 1) * 3;
      const start = `${year}-${String(sm + 1).padStart(2, "0")}-01`;
      const endDate = new Date(Date.UTC(year, sm + 3, 0));
      return `start=${start}&end=${endDate.toISOString().slice(0, 10)}`;
    }
    return `year=${year}`;
  }, [mode, year, quarter, from, to]);

  useEffect(() => {
    if (!query) { setRep(null); return; }
    api.get(`/cash/deposit-report?${query}`).then(({ data }) => setRep(data)).catch(() => setRep(null));
    setActiveQuarter(null);
  }, [query]);

  const rowQuarter = (date) => `${date.slice(0, 4)} · Q${Math.floor((+date.slice(5, 7) - 1) / 3) + 1}`;
  const detailRows = useMemo(() => {
    if (!rep) return [];
    return activeQuarter ? rep.rows.filter((r) => rowQuarter(r.date) === activeQuarter) : rep.rows;
  }, [rep, activeQuarter]);

  const groups = useMemo(() => {
    if (!rep) return [];
    const map = new Map();
    rep.rows.forEach((r) => {
      const k = group === "month" ? r.date.slice(0, 7) : isoWeek(r.date);
      const label = group === "month" ? `${r.date.slice(5, 7)}/${r.date.slice(0, 4)}` : k.replace("-W", " · W");
      const g = map.get(k) || { key: k, label, total: 0, count: 0 };
      g.total = Math.round((g.total + r.amount) * 100) / 100; g.count += 1;
      map.set(k, g);
    });
    return [...map.values()].sort((a, b) => a.key.localeCompare(b.key));
  }, [rep, group]);

  const max = Math.max(1, ...groups.map((g) => g.total));
  const fileBase = rep?.label ? rep.label.replace(/\s→\s/, "_").replace(/\s/g, "") : year;

  const quarters = useMemo(() => {
    if (!rep) return [];
    const rate = rep.vat_rate || 0;
    const map = new Map();
    rep.rows.forEach((r) => {
      const q = `${r.date.slice(0, 4)} · Q${Math.floor((+r.date.slice(5, 7) - 1) / 3) + 1}`;
      map.set(q, (map.get(q) || 0) + r.amount);
    });
    return [...map.entries()].sort((a, b) => a[0].localeCompare(b[0])).map(([k, incl]) => {
      const vat = rate ? Math.round((incl * rate) / (100 + rate) * 100) / 100 : 0;
      return { key: k, incl: Math.round(incl * 100) / 100, excl: Math.round((incl - vat) * 100) / 100, vat };
    });
  }, [rep]);

  const exportCsv = () => {
    if (!rep) return;
    const head = ["datum", "gast", "plaats", "bedrag", "verrekend", "factuur"];
    const lines = rep.rows.map((r) => [r.date, r.guest_name || "", r.pitch_label || "", r.amount.toFixed(2), r.settled ? "ja" : "nee", r.invoice_number || ""].join(";"));
    const blob = new Blob([[head.join(";"), ...lines].join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a"); a.href = url; a.download = `voorschotten-${fileBase}.csv`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 10000);
  };

  const exportPdf = async () => {
    if (!query) return;
    try {
      const { data } = await api.get(`/cash/deposit-report.pdf?${query}`, { responseType: "blob" });
      const url = URL.createObjectURL(data);
      window.open(url, "_blank");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch { /* genegeerd */ }
  };

  return (
    <Panel
      title={`${t("admin_prepayments")} · ${rep?.label || year}`}
      testid="prepayments-panel"
      action={
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={exportCsv} data-testid="prepay-export"><Download className="h-4 w-4 mr-1" />CSV</Button>
          <Button variant="outline" size="sm" onClick={exportPdf} data-testid="prepay-export-pdf"><FileText className="h-4 w-4 mr-1" />PDF</Button>
        </div>
      }
    >
      <div className="px-5 pt-4 flex flex-wrap items-center gap-2" data-testid="prepay-filters">
        <div className="inline-flex rounded-lg border border-border overflow-hidden">
          {[["year", t("per_year")], ["quarter", t("per_quarter")], ["custom", t("custom_range")]].map(([m, lbl]) => (
            <button key={m} onClick={() => setMode(m)} data-testid={`prepay-mode-${m}`}
              className={`px-3 py-1.5 text-xs font-semibold transition-colors ${mode === m ? "bg-primary text-primary-foreground" : "bg-card text-muted-foreground hover:bg-muted"}`}>
              {lbl}
            </button>
          ))}
        </div>
        {mode !== "custom" && (
          <div className="inline-flex items-center gap-1">
            <Button variant="ghost" size="sm" onClick={() => setYear(year - 1)} data-testid="prepay-prev-year">‹</Button>
            <b className="tabular-nums text-sm" data-testid="prepay-year">{year}</b>
            <Button variant="ghost" size="sm" onClick={() => setYear(year + 1)} data-testid="prepay-next-year">›</Button>
          </div>
        )}
        {mode === "quarter" && (
          <div className="inline-flex rounded-lg border border-border overflow-hidden">
            {[1, 2, 3, 4].map((q) => (
              <button key={q} onClick={() => setQuarter(q)} data-testid={`prepay-q-${q}`}
                className={`px-3 py-1.5 text-xs font-semibold transition-colors ${quarter === q ? "bg-secondary text-primary" : "bg-card text-muted-foreground hover:bg-muted"}`}>
                Q{q}
              </button>
            ))}
          </div>
        )}
        {mode === "custom" && (
          <div className="inline-flex items-center gap-1.5 text-xs">
            <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} data-testid="prepay-from"
              className="rounded-md border border-border bg-card px-2 py-1.5" />
            <span className="text-muted-foreground">→</span>
            <input type="date" value={to} onChange={(e) => setTo(e.target.value)} data-testid="prepay-to"
              className="rounded-md border border-border bg-card px-2 py-1.5" />
          </div>
        )}
      </div>
      {mode === "custom" && (!from || !to) ? <div className="p-5 text-sm text-muted-foreground" data-testid="prepay-pick-range">{t("pick_range_hint")}</div>
        : !rep ? <Empty /> : (
        <div className="p-5 space-y-5">
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {[
              ["total", t("deposit"), rep.totals.total, "text-sky-600"],
              ["count", t("count"), rep.totals.count, "text-slate-500"],
              ["settled", t("deposit_settled"), rep.totals.settled, "text-emerald-600"],
              ["unsettled", t("deposit_unsettled"), rep.totals.unsettled, "text-amber-600"],
            ].map(([k, label, val, cls]) => (
              <div key={k} className="rounded-xl border border-border bg-card p-3" data-testid={`prepay-kpi-${k}`}>
                <p className="text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground">{label}</p>
                <p className={`font-display font-extrabold text-lg tabular-nums ${cls}`}>{k === "count" ? val : money(val)}</p>
              </div>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <Button variant={group === "month" ? "default" : "outline"} size="sm" onClick={() => setGroup("month")} data-testid="prepay-group-month">{t("by_month")}</Button>
            <Button variant={group === "week" ? "default" : "outline"} size="sm" onClick={() => setGroup("week")} data-testid="prepay-group-week">{t("by_week")}</Button>
          </div>

          {groups.some((g) => g.total > 0) ? (
            <div className="rounded-xl border border-border bg-card p-4">
              <div className="flex items-end gap-2" style={{ height: 160 }}>
                {groups.map((g) => (
                  <div key={g.key} className="flex-1 flex flex-col items-center justify-end gap-1" title={`${g.label} · ${money(g.total)}`} data-testid={`prepay-bar-${g.key}`}>
                    <span className="text-[9px] tabular-nums text-muted-foreground">{g.total ? Math.round(g.total) : ""}</span>
                    <div className="w-full rounded-t bg-sky-500" style={{ height: Math.max(3, (g.total / max) * 120) }} />
                    <span className="text-[9px] font-bold text-muted-foreground truncate w-full text-center">{g.label.split(" · ").pop()}</span>
                  </div>
                ))}
              </div>
            </div>
          ) : <p className="text-sm text-muted-foreground">{t("no_deposits_year")}</p>}

          {quarters.length > 0 && (
            <div className="rounded-xl border border-border bg-card overflow-hidden" data-testid="prepay-vat-breakdown">
              <p className="px-3 py-2 text-[11px] font-bold uppercase tracking-[0.16em] text-muted-foreground bg-muted/40">
                {t("vat_breakdown")} · {t("deposit")} {t("btw")} {rep.vat_rate}%
              </p>
              <table className="w-full text-[12px]">
                <thead>
                  <tr className="text-muted-foreground">
                    <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("period")}</th>
                    <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("incl_vat")}</th>
                    <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("excl_vat")}</th>
                    <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("btw")}</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {quarters.map((q) => (
                    <tr key={q.key} data-testid={`prepay-vat-${q.key.replace(/[^0-9Q]/g, "")}`}
                      onClick={() => setActiveQuarter(activeQuarter === q.key ? null : q.key)}
                      className={`cursor-pointer transition-colors ${activeQuarter === q.key ? "bg-sky-50 dark:bg-sky-950/30" : "hover:bg-muted/50"}`}
                      title={t("drilldown_hint")}>
                      <td className="px-3 py-2 font-semibold">{q.key}{activeQuarter === q.key ? " ▾" : ""}</td>
                      <td className="px-3 py-2 text-right tabular-nums font-semibold text-sky-700">{money(q.incl)}</td>
                      <td className="px-3 py-2 text-right tabular-nums">{money(q.excl)}</td>
                      <td className="px-3 py-2 text-right tabular-nums text-slate-500">{money(q.vat)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div className="overflow-auto cb-scroll">
            {activeQuarter && (
              <div className="flex items-center gap-2 px-1 pb-2 text-xs" data-testid="prepay-filter-active">
                <span className="text-muted-foreground">{t("showing")}:</span>
                <span className="inline-flex items-center gap-1 rounded-full bg-sky-100 text-sky-800 px-2 py-0.5 font-semibold">{activeQuarter}</span>
                <button onClick={() => setActiveQuarter(null)} data-testid="prepay-filter-clear" className="text-primary hover:underline">{t("show_all")}</button>
              </div>
            )}
            <table className="w-full text-[12px]">
              <thead>
                <tr className="bg-muted/40 text-muted-foreground">
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("date")}</th>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("guest")}</th>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("pitch")}</th>
                  <th className="text-right px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("deposit")}</th>
                  <th className="text-left px-3 py-2 font-bold uppercase tracking-wider text-[10px]">{t("deposit_settled")}</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {detailRows.length === 0 && <tr><td colSpan={5} className="px-3 py-6 text-center text-muted-foreground">{t("no_deposits_year")}</td></tr>}
                {detailRows.map((r, i) => (
                  <tr key={i} data-testid={`prepay-row-${i}`}>
                    <td className="px-3 py-2 tabular-nums">{r.date}</td>
                    <td className="px-3 py-2">{r.guest_name || "—"}</td>
                    <td className="px-3 py-2">{r.pitch_label || "—"}</td>
                    <td className="px-3 py-2 text-right tabular-nums font-semibold text-sky-700">{money(r.amount)}</td>
                    <td className="px-3 py-2">
                      {r.settled
                        ? <span className="text-emerald-600">{r.invoice_number || t("yes")}</span>
                        : <span className="text-amber-600">{t("no")}</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className="text-[11px] text-muted-foreground flex items-center gap-1.5">
            <Wallet className="h-3.5 w-3.5" />{t("admin_prepayments_desc")}
          </p>
        </div>
      )}
    </Panel>
  );
}
